import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { changePassword } from '../api';

export default function MyAccount() {
  const { user } = useAuth();
  const [form, setForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [show, setShow] = useState({ current: false, next: false, confirm: false });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    if (form.new_password !== form.confirm_password) {
      setError('New password and confirmation do not match');
      return;
    }
    setLoading(true);
    try {
      await changePassword({ current_password: form.current_password, new_password: form.new_password });
      setSuccess('✅ Password changed successfully');
      setForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const roleLabel = user?.role === 'superadmin' ? 'Super Admin' : user?.role === 'staff' ? 'Staff' : user?.role;
  const roleBadge = user?.role === 'superadmin'
    ? { color: 'var(--color-info)', background: 'rgba(37,99,235,0.10)', borderColor: 'rgba(37,99,235,0.22)' }
    : { color: 'var(--accent)', background: 'rgba(79,140,255,0.10)', borderColor: 'rgba(79,140,255,0.22)' };

  return (
    <div>
      <style>{`
        .acc-input:focus { outline:none; border-color:var(--accent)!important; box-shadow:0 0 0 3px var(--accent-bg)!important; }
        .submit-btn:hover:not(:disabled) { background:var(--bg-elevated)!important; border-color:var(--accent)!important; }
        .eye-btn:hover { color:var(--text-primary)!important; }
      `}</style>

      <div style={s.topbar}>
        <div className="page-eyebrow">SETTINGS</div><h1 className="page-title">My Account</h1>
      </div>

      {/* Info card */}
      <div style={s.infoCard}>
        <div style={s.avatar}>{user?.name?.[0]}</div>
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontSize:18,fontWeight:700,color:'var(--text-primary)',marginBottom:4}}>{user?.name}</div>
          <div style={{display:'flex',gap:10,alignItems:'center',flexWrap:'wrap'}}>
            <span style={{fontVariantNumeric:"tabular-nums",fontWeight:600,fontSize:12,color:'var(--accent)',fontWeight:600}}>{user?.code}</span>
            <span style={{color:roleBadge.color,background:roleBadge.background,border:`1px solid ${roleBadge.borderColor}`,padding:'3px 9px',borderRadius:6,fontSize:11,fontWeight:700}}>
              {roleLabel}
            </span>
          </div>
        </div>
      </div>

      {/* Change password form */}
      <div style={s.formCard}>
        <div style={s.formTitle}>CHANGE PASSWORD</div>
        <form onSubmit={handleSubmit}>
          <PasswordField label="CURRENT PASSWORD" value={form.current_password} onChange={v=>setForm(f=>({...f,current_password:v}))} show={show.current} toggle={()=>setShow(s=>({...s,current:!s.current}))} />
          <PasswordField label="NEW PASSWORD" value={form.new_password} onChange={v=>setForm(f=>({...f,new_password:v}))} show={show.next} toggle={()=>setShow(s=>({...s,next:!s.next}))} />
          <PasswordField label="CONFIRM NEW PASSWORD" value={form.confirm_password} onChange={v=>setForm(f=>({...f,confirm_password:v}))} show={show.confirm} toggle={()=>setShow(s=>({...s,confirm:!s.confirm}))} />

          {error && <div style={s.error}>{error}</div>}
          {success && <div style={s.success}>{success}</div>}

          <button className="submit-btn" type="submit" disabled={loading||!form.current_password||!form.new_password||!form.confirm_password}
            style={{...s.submitBtn,opacity:(loading||!form.current_password||!form.new_password||!form.confirm_password)?0.5:1}}>
            {loading ? 'Saving...' : '🔑 Change password'}
          </button>
        </form>
      </div>
    </div>
  );
}

function PasswordField({ label, value, onChange, show, toggle }) {
  return (
    <div style={{marginBottom:14}}>
      <label style={s.label}>{label}</label>
      <div style={{position:'relative'}}>
        <input className="acc-input" type={show?'text':'password'} value={value} onChange={e=>onChange(e.target.value)} style={{...s.input,paddingRight:40}} />
        <button type="button" className="eye-btn" onClick={toggle}
          style={{position:'absolute',right:8,top:'50%',transform:'translateY(-50%)',background:'none',border:'none',color:'var(--text-muted)',cursor:'pointer',fontSize:16,padding:'4px 6px'}}
          title={show?'Hide':'Show'}>
          {show ? '🙈' : '👁'}
        </button>
      </div>
    </div>
  );
}

const s = {
  topbar: { marginBottom:22 },
  pageLabel: { fontSize:10, fontWeight:700, color:'var(--text-muted)', letterSpacing:2, textTransform:'uppercase', marginBottom:4 },
  pageTitle: { fontSize:22, fontWeight:600, color:'var(--text-primary)', letterSpacing:-0.3 },
  infoCard: { display:'flex', alignItems:'center', gap:14, padding:'18px 22px', marginBottom:20, background:'var(--bg-secondary)', border:'1px solid rgba(15,23,42,0.07)', borderRadius:12 },
  avatar: { width:48, height:48, borderRadius:'50%', background:'var(--accent-bg)', border:'1px solid rgba(94,161,255,0.3)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:20, fontWeight:700, color:'var(--accent)', fontVariantNumeric:"tabular-nums",fontWeight:600, flexShrink:0 },
  formCard: { background:'var(--bg-secondary)', border:'1px solid rgba(15,23,42,0.07)', borderRadius:12, padding:'22px 24px', maxWidth:480 },
  formTitle: { fontSize:10, fontWeight:700, color:'var(--text-muted)', letterSpacing:1.8, marginBottom:16 },
  label: { display:'block', fontSize:10, fontWeight:700, color:'var(--text-muted)', letterSpacing:1, textTransform:'uppercase', marginBottom:6 },
  input: { width:'100%', background:'var(--bg-tertiary)', border:'1px solid rgba(15,23,42,0.08)', borderRadius:8, padding:'9px 12px', fontSize:13, color:'var(--text-primary)',  transition:'all 0.18s', boxSizing:'border-box' },
  error: { background:'rgba(220,38,38,0.08)', color:'#b91c1c', borderLeft:'2px solid #ff5252', padding:'8px 12px', borderRadius:6, fontSize:12, marginBottom:12 },
  success: { background:'var(--accent-bg)', color:'var(--accent)', borderLeft:'2px solid var(--accent)', padding:'8px 12px', borderRadius:6, fontSize:12, marginBottom:12 },
  submitBtn: { width:'100%', background:'var(--bg-tertiary)', color:'var(--text-primary)', border:'1px solid var(--accent-strong)', borderRadius:8, padding:'11px', fontSize:13, fontWeight:700, cursor:'pointer', transition:'all 0.18s',  },
};
